import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Map, MessageSquare, FileText } from "lucide-react";

const NAV_ITEMS = [
  { path: "/ressourcehandtering", icon: Map, label: "Kort" },
  { path: "/beskeder", icon: MessageSquare, label: "Beskeder" },
  { path: "/vigtige-info", icon: FileText, label: "Info" },
];

const AUTO_HIDE_MS = 4000;

export default function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [hideTimer, setHideTimer] = useState(null);

  const showNav = useCallback(() => {
    setVisible(true);
    if (hideTimer) clearTimeout(hideTimer);
    const timer = setTimeout(() => setVisible(false), AUTO_HIDE_MS);
    setHideTimer(timer);
  }, [hideTimer]);

  // Vis nav ved tryk i bunden af skærmen (nederste 60px)
  useEffect(() => {
    const handleTouch = (e) => {
      const y = e.touches?.[0]?.clientY || e.clientY;
      if (y > window.innerHeight - 60) showNav();
    };
    window.addEventListener("touchstart", handleTouch, { passive: true });
    return () => window.removeEventListener("touchstart", handleTouch);
  }, [showNav]);

  // Ryd timer ved unmount
  useEffect(() => {
    return () => { if (hideTimer) clearTimeout(hideTimer); };
  }, [hideTimer]);

  // Kun vis på mobil
  if (typeof window !== "undefined" && window.innerWidth >= 768) return null;

  // Kun vis inden i en event-kontekst (ikke på login, landing, etc.)
  const eventPages = ["/ressourcehandtering", "/beskeder", "/vigtige-info", "/dashboard", "/frivillige", "/vagtplaner", "/analytics", "/kalender", "/materiel", "/lager", "/stamdata", "/leder", "/setup-log", "/platform-settings", "/idebank"];
  const isEventPage = eventPages.some(p => location.pathname.startsWith(p));
  if (!isEventPage) return null;

  return (
    <>
      {/* Lille indikator-prik når nav er skjult */}
      {!visible && (
        <div
          className="fixed bottom-2 left-1/2 -translate-x-1/2 z-50 md:hidden"
          onClick={showNav}
          data-testid="mobile-nav-indicator"
        >
          <div className="w-10 h-1 bg-white/30 rounded-full" />
        </div>
      )}

      {/* Auto-hiding bundnavigation */}
      <nav
        className={`fixed bottom-0 left-0 right-0 z-50 md:hidden transition-transform duration-300 ${visible ? "translate-y-0" : "translate-y-full"}`}
        data-testid="mobile-bottom-nav"
      >
        <div className="bg-zinc-900/95 backdrop-blur-md border-t border-zinc-700/50 px-4 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <div className="flex justify-around items-center">
            {NAV_ITEMS.map(({ path, icon: Icon, label }) => {
              const isActive = location.pathname === path;
              return (
                <button
                  key={path}
                  onClick={() => { navigate(path); showNav(); }}
                  className={`flex flex-col items-center gap-0.5 px-4 py-1.5 rounded-lg transition-colors ${isActive ? "text-primary" : "text-zinc-400 active:text-white"}`}
                  data-testid={`mobile-nav-${label.toLowerCase()}`}
                >
                  <Icon className={`h-5 w-5 ${isActive ? "text-primary" : ""}`} />
                  <span className="text-[10px] font-medium">{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
}
